import { InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { FaCogs } from "react-icons/fa";
import { FiEdit, FiEye, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { WorkOrderInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { statusColor } from "../../common/utils/orderStatusColor";
import ConfirmDialog from "../../components/ConfirmDialog";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import { TypeOrderStatus } from "../../components/enum/order";
import HelpVideoButton from "../../components/HelpVideoButton";
import WorkOrderCreateModal from "../../components/modal/WorkOrderCreateModal";
import PaginationControls from "../../components/PaginationControls";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ActionButton,
  Container,
  FiltersContainer,
  FormControlWorkOrderSearch,
  StatusBadge,
  Table,
  TableContainer,
  TextFeldWorkOrderSearch,
  Title,
  WorkOrderSearchCard
} from "./workorder.styled";

export const WorkOrderPage = () => {
  const [status, setStatus] = useState<keyof typeof TypeOrderStatus>("NONE");
  const [orders, setOrders] = useState<WorkOrderInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrderInterface | null>(
    null
  );
  const [clientSearch, setClientSearch] = useState("");
  const [workOrderSearch, setWorkOrderSearch] = useState("");

  const [confirmDeleteOrder, setConfirmDeleteOrder] = useState(false);

  const [orderSelectToRemove, setOrderSelectToRemove] = useState<any>(null);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const containerId = "toaster-workorder";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const navigate = useNavigate();
  const { isUser, isAdmin } = useAuth();

  const fetchOrders = async (currentPage: number) => {
    try {
      const response = await api.get("/work-order", {
        params: {
          page: currentPage,
          limit: 8,
          client_name:
            clientSearch.trim() !== "" ? clientSearch.trim() : undefined,
          work_order_number:
            Number(workOrderSearch.trim()) !== 0
              ? Number(workOrderSearch.trim())
              : undefined,
          work_order_status: status,
          initial_date: startDate ? startDate : undefined,
          final_date: endDate ? endDate : undefined
        }
      });

      setOrders(response.data.items);

      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);
      console.error("Erro ao buscar ordens de serviço:", error);
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const response = await api.get(`/work-order/${id}`);

      setSelectedOrder(response.data);
      setIsModalOpen(true);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);
      console.error("Erro ao carregar ordem de serviço:", error);
    }
  };

  const handleDeleteOrder = async () => {
    try {
      await api.delete(`/work-order/${orderSelectToRemove}`);
      setOrders(
        orders.filter((order) => order.work_order_id !== orderSelectToRemove)
      );
      autoSuccess("Ordem de serviço excluída com sucesso.");
      setConfirmDeleteOrder(false);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);
      console.error("Erro ao excluir ordem de serviço:", error);
      setConfirmDeleteOrder(false);
    }
  };

  const askRemoveOrder = (worder_id: any) => {
    setOrderSelectToRemove(worder_id);
    setConfirmDeleteOrder(true);
  };

  useEffect(() => {
    fetchOrders(page);
  }, [page, clientSearch, workOrderSearch, status, startDate, endDate]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Ordens de Serviço
        <CreateButton
          onClick={() => setIsModalOpen(true)}
          style={{ marginLeft: "14px" }}
        >
          <FaCogs size={16} /> Criar Ordem
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/9nDtT4Uuzjw"
          modalTitle="Como criar uma Ordem de Serviço"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <WorkOrderSearchCard>
        <FiltersContainer>
          <TextField
            label="Data de Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toLocaleDateString("en-CA") // impede datas futuras no fuso local
            }}
          />

          <TextField
            disabled={!startDate}
            label="Data Final"
            type="date"
            value={startDate ? endDate : ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate || undefined, // impede menor que startDate
              max: new Date().toLocaleDateString("en-CA") // impede datas futuras
            }}
          />

          {/* </div> */}

          <FormControlWorkOrderSearch>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) =>
                setStatus(e.target.value as keyof typeof TypeOrderStatus)
              }
            >
              {Object.entries(TypeOrderStatus).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {key === "NONE" ? "Todos" : label}
                </MenuItem>
              ))}
            </Select>
          </FormControlWorkOrderSearch>

          <TextFeldWorkOrderSearch
            label="Nome do cliente"
            variant="outlined"
            value={clientSearch}
            onChange={(e) => {
              setClientSearch(e.target.value);
              setPage(1);
            }}
          />

          <TextFeldWorkOrderSearch
            sx={{ width: "150px" }}
            label="Ordem"
            variant="outlined"
            value={workOrderSearch}
            onChange={(e) => {
              setWorkOrderSearch(e.target.value);
              setPage(1);
            }}
          />

          {/* Se quiser adicionar busca por nome do usuário também */}
          {/* <TextFeldWorkOrderSearch label="Nome do usuário" ... /> */}
        </FiltersContainer>
      </WorkOrderSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Ordem</th>
              {isAdmin && <th>Empresa</th>}
              <th>Cliente</th>
              <th>Data de Início</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.work_order_id}>
                <td>{order.order_number}</td>
                {isAdmin && <td>{order.client.company.company_name}</td>}
                <td>{order.client.client_name}</td>
                <td>
                  {new Date(order.start_date).toLocaleDateString("pt-BR")}
                </td>
                <td>
                  <StatusBadge $color={statusColor(order.status)}>
                    {TypeOrderStatus[
                      order.status as keyof typeof TypeOrderStatus
                    ] || order.status}
                  </StatusBadge>
                </td>

                <td>
                  {!isUser && (
                    <CustomTooltip title="Editar Ordem de Serviço">
                      <ActionButton
                        onClick={() => handleEdit(order.work_order_id)}
                        style={{ marginRight: "10px" }}
                      >
                        <FiEdit size={20} color="#4caf50" />
                      </ActionButton>
                    </CustomTooltip>
                  )}

                  <CustomTooltip title="Visualizar Ordem de Serviço">
                    <ActionButton
                      onClick={() =>
                        navigate(`/workorder-details/${order.work_order_id}`)
                      }
                    >
                      <FiEye size={20} color="#2196f3" />
                    </ActionButton>
                  </CustomTooltip>

                  {!isUser && (
                    <CustomTooltip title="Excluir Ordem de Serviço">
                      <ActionButton
                        onClick={() => askRemoveOrder(order.work_order_id)}
                      >
                        <FiTrash2 size={20} color="#f44336" />
                      </ActionButton>
                    </CustomTooltip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <WorkOrderCreateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedOrder(null);
        }}
        onWorkOrderCreated={() => fetchOrders(page)}
        order={selectedOrder ?? undefined}
      />

      <ConfirmDialog
        open={confirmDeleteOrder}
        title="Confirmar Exclusão"
        description={`Deseja mesmo remover esta ordem de serviço?`}
        onClose={() => setConfirmDeleteOrder(false)}
        onConfirm={handleDeleteOrder}
      />
    </Container>
  );
};
